// TasksTodayPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Select, TextInput, Tooltip } from "flowbite-react";
import { motion } from "framer-motion";
import axios from "../config/axiosInstance";
import { API, CRM, USERS } from "../config/apiConfig";
import TiptapEditor from "../components/inputs/TipTapEditor";
import {
  HiMail,
  HiViewList,
  HiX,
  HiSearch,
  HiUser,
  HiCalendar,
  HiClock,
  HiCheck,
  HiPhone,
  HiMicrophone,
  HiVideoCamera,
  HiUserGroup,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi";
import {
  HiCalendarDays,
  HiMiniPencilSquare,
  HiOutlineSparkles,
} from "react-icons/hi2";
import TaskModal from "../components/modals/TaskModal";
import { useApplication } from "../contexts/ApplicationContext";
import { toRichHtml, stripVisualEmpty } from "../utils/utils";

/* ===================== Datas helpers ===================== */
const pad2 = (n) => String(n).padStart(2, "0");
const normalizeIso = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.toISOString();
  return "";
};
const toISODate = (d) => {
  const iso = normalizeIso(d);
  return iso.slice(0, 10);
};
// Constrói Date no fuso local a partir de "YYYY-MM-DD"
function fromISODateLocal(iso) {
  if (!iso) return new Date(NaN);
  if (iso instanceof Date) return new Date(iso);
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
const toBRDate = (iso) => {
  const normalized = normalizeIso(iso);
  if (!normalized) return "—";
  const datePart = normalized.split("T")[0].split(" ")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}/${month}/${year}`;
};
const addDays = (iso, days) => {
  const d = fromISODateLocal(iso);
  d.setDate(d.getDate() + days);
  return toISODate(d);
};
const sameDay = (isoA, isoB) =>
  String(isoA).slice(0, 10) === String(isoB).slice(0, 10);

function startOfWeekISO(iso, weekStartsOn = 1 /* Monday */) {
  const d = fromISODateLocal(iso);
  const day = d.getDay(); // 0..6 (Sun..Sat)
  const current = day === 0 ? 7 : day; // 1..7 (Mon..Sun)
  const diff = current - weekStartsOn;
  d.setDate(d.getDate() - diff);
  return toISODate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
}
function endOfWeekISO(iso, weekStartsOn = 1) {
  const start = fromISODateLocal(startOfWeekISO(iso, weekStartsOn));
  start.setDate(start.getDate() + 6);
  return toISODate(start);
}
function startOfMonthISO(iso) {
  const d = fromISODateLocal(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}
function endOfMonthISO(iso) {
  const d = fromISODateLocal(iso);
  return toISODate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

function getTaskDisplayTitle(task) {
  // 1) se tiver card, usa lead
  const fromLead =
    task?.card?.lead?.name || task?.card?.lead?.companyName || null;

  // 2) tenta algo do próprio task (caso você tenha title, subject, note, etc.)
  const fromTask =
    task?.title ||
    task?.subject ||
    (typeof task?.note === "string"
      ? task.note.replace(/<[^>]+>/g, "").slice(0, 80)
      : null);

  // 3) fallback
  return fromLead || fromTask || "Tarefa sem card";
}

function stripHtmlPreview(html = "", max = 200) {
  const text = String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "—";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/* ============================================================
 * Stage helpers (robustos)
 * ============================================================ */
function isDueToday(task, refISO) {
  if (!task?.dueDate || !refISO) return false;
  if (task?.status !== "open") return false;
  return sameDay(task?.dueDate, refISO);
}

/* Month matrix (Mon-Sun) */
function buildMonthMatrix(anchorISO) {
  const startMonth = fromISODateLocal(startOfMonthISO(anchorISO));
  const endMonth = fromISODateLocal(endOfMonthISO(anchorISO));
  const start = fromISODateLocal(startOfWeekISO(startMonth, 1));
  const end = fromISODateLocal(endOfWeekISO(endMonth, 1));

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= end) {
    const row = [];
    for (let i = 0; i < 7; i++) {
      row.push(toISODate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

/* ============================================================
 * Filtros persistidos
 * ============================================================ */
const LS_KEY = "tasksToday.filters.v3";
function readFilters() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function writeFilters(filters) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(filters));
  } catch {}
}

/* ============================================================
 * Lógica de atraso / campos visuais
 * ============================================================ */
function isOverdueTask(task, refISO) {
  if (!task?.dueDate || !refISO) return false;
  if (task?.status !== "open") return false;
  const dueISO = toISODate(task.dueDate);
  return !!dueISO && dueISO < String(refISO).slice(0, 10);
}

const TYPE_LABEL = {
  ligacao: "Ligação",
  reuniao: "Reunião",
  email: "E-mail",
  visita: "Visita",
  followup: "Follow-up",
};
const TYPE_ICON = {
  ligacao: HiPhone,
  reuniao: HiVideoCamera,
  email: HiMail,
  visita: HiUserGroup,
  followup: HiClock,
};
const TYPE_ACCENT = {
  ligacao: "from-red-500 to-pink-500",
  reuniao: "from-indigo-500 to-violet-500",
  email: "from-sky-500 to-cyan-500",
  visita: "from-emerald-500 to-lime-500",
  followup: "from-amber-500 to-orange-500",
  default: "from-gray-400 to-gray-500",
};
const statusChip = (status) => {
  if (status === "done")
    return "bg-green-50 text-green-700 border border-green-200";
  if (status === "canceled")
    return "bg-rose-50 text-rose-700 border border-rose-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
};
function extractTime(isoStr) {
  if (!isoStr) return { hour: null, minute: null, label: "—", hasTime: false };
  const normalized = normalizeIso(isoStr);
  const match =
    normalized.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/) ||
    normalized.match(/\s(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return { hour: null, minute: null, label: "—", hasTime: false };
  const [_, h, m] = match;
  const hour = Number(h);
  const minute = Number(m);
  if (Number.isNaN(hour) || Number.isNaN(minute))
    return { hour: null, minute: null, label: "—", hasTime: false };
  return {
    hour,
    minute,
    label: `${pad2(hour)}:${pad2(minute)}`,
    hasTime: true,
  };
}

/* Agrupa por hora (para modo Dia) */
function groupByHour(tasks) {
  const byHour = new Map();
  const noTime = [];
  let minH = 24,
    maxH = 0,
    hasAnyTime = false;

  tasks.forEach((t) => {
    const tm = extractTime(t?.dueDate);
    if (!tm.hasTime || tm.hour === null) {
      noTime.push(t);
      return;
    }
    hasAnyTime = true;
    minH = Math.min(minH, tm.hour);
    maxH = Math.max(maxH, tm.hour);
    if (!byHour.has(tm.hour)) byHour.set(tm.hour, []);
    byHour.get(tm.hour).push(t);
  });

  if (!hasAnyTime) return { byHour, noTime, hoursWindow: [9, 18] };

  const start = Math.max(6, Math.min(9, minH) - 1);
  const end = Math.min(22, Math.max(18, maxH) + 1);
  return { byHour, noTime, hoursWindow: [start, end] };
}

async function fetchStagesFromFunnel(funnelId) {
  if (!funnelId) return [];
  try {
    const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${funnelId}/details`);
    const data = res?.data?.data || {};
    // Normaliza como no seu fetchFunnel (aqui ignoramos os cards e mantemos id/name/order/color)
    const stages = (data.stages || []).map((st, i) => ({
      id: st?.id,
      name: st?.name ?? `Etapa ${i + 1}`,
      color: st?.color ?? undefined,
      order: Number.isFinite(+st?.order)
        ? +st.order
        : Number.isFinite(+st?.position)
        ? +st.position
        : i + 1,
    }));
    // Ordena por order
    return stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } catch (err) {
    console.error("Erro ao buscar stages do funil:", err);
    return [];
  }
}

/* ============================================================
 * Peças visuais
 * ============================================================ */
function EmptyState({ dateBR, label = "Nada" }) {
  return (
    <div className="text-center py-16">
      <div className="text-5xl mb-3">✨</div>
      <h3 className="text-lg font-semibold text-gray-800">
        {label} em {dateBR}
      </h3>
      <p className="text-gray-500">Sem tarefas para o período selecionado.</p>
    </div>
  );
}
const SkeletonRow = () => (
  <div className="grid grid-cols-[88px,1fr] gap-4 items-start">
    <div className="h-6 bg-gray-100 rounded w-full" />
    <div className="h-16 bg-gray-100 rounded" />
  </div>
);
const ResponsibleChip = ({ name, isWeek }) => {
  const assigned = !!(name && String(name).trim());
  if (!assigned) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-dashed border-gray-300 ${
          isWeek ? "text-[10px]" : "text-[12px]"
        } text-gray-500`}
      >
        <HiUser className={`${isWeek ? "h-3 w-3" : "h-4 w-4"}`} />
        Não atribuído
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50  text-gray-800 ${
        isWeek ? "text-[10px]" : "text-[12px]"
      }`}
    >
      <HiUser className={`${isWeek ? "h-3 w-3" : "h-4 w-4"}`} />
      {name}
    </span>
  );
};
const TypePill = ({ type }) => {
  const TypeIcon = TYPE_ICON[type] || HiClock;
  const typeLabel = TYPE_LABEL[type] || "Ação";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border bg-white">
      <TypeIcon className="w-3.5 h-3.5 text-gray-600" />
      <span className="text-gray-700">{typeLabel}</span>
    </span>
  );
};
function AvatarInitials({ name = "" }) {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  const initials = parts.map((p) => p[0]?.toUpperCase() || "").join("");
  return (
    <div className="h-6 w-6 rounded-full bg-gray-200 text-gray-700 text-[11px] font-semibold flex items-center justify-center">
      {initials || "?"}
    </div>
  );
}
function IconActionButton({ title, onClick, children, color = "light" }) {
  return (
    <Button
      color={color}
      size="xs"
      onClick={onClick}
      className="!p-1.5"
      title={title}
      aria-label={title}
    >
      {children}
    </Button>
  );
}
function StatusPill({ status }) {
  const map = {
    open: "bg-blue-50 text-blue-700 border-blue-200",
    done: "bg-green-50 text-green-700 border-green-200",
    canceled: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const label =
    status === "done"
      ? "Concluída"
      : status === "canceled"
      ? "Cancelada"
      : "Aberta";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full border ${
        map[status] || "bg-gray-50 text-gray-700 border-gray-200"
      }`}
    >
      {label}
    </span>
  );
}

/* Card de tarefa */
function CalendarTaskCard({
  task,
  overdue,
  onDone,
  onCancel,
  onOpenCard,
  compact = false,
  isWeek = false,
}) {
  const type = task?.type || "default";
  const tm = extractTime(task?.dueDate);
  const dueStr = toBRDate(task?.dueDate);
  const statusLabel =
    task?.status === "done"
      ? "Concluída"
      : task?.status === "canceled"
      ? "Cancelada"
      : "Aberta";
  const taskResponsibleName =
    task?.responsible?.name || task?.card?.responsible?.name || "";
  const companyName = getTaskDisplayTitle(task);
  const hasCard = !!task?.card?.id;

  if (compact) {
    // Versão compacta (sem footer de ações) – ideal para Semana/Mês e mobile
    return (
      <div
        className={[
          "rounded-xl border bg-white shadow-sm hover:shadow-md transition-shadow p-2",
          overdue ? "border-rose-300 ring-1 ring-rose-200" : "border-gray-200",
        ].join(" ")}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 min-w-0">
            {!isWeek && (
              <div
                className={`w-1.5 h-4 rounded bg-gradient-to-b ${
                  TYPE_ACCENT[type] || TYPE_ACCENT.default
                }`}
              />
            )}

            <TypePill type={type} />
            {!isWeek && (
              <span className="text-[11px] text-gray-500 tabular-nums">
                {tm.label}
              </span>
            )}
          </div>
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusChip(
              task?.status
            )}`}
          >
            {statusLabel}
          </span>
        </div>
        <div
          className={`font-semibold text-gray-700 ${
            hasCard ? "cursor-pointer" : "cursor-default"
          } truncate ${isWeek ? "text-[10px]" : "text-[13px]"}`}
          onClick={hasCard ? () => onOpenCard?.(task) : undefined}
          title={hasCard ? "Ver card" : companyName}
        >
          {companyName}
        </div>
        {!isWeek && (
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-700">
            <HiCalendar className="w-4 h-4" />
            <b className={overdue ? "text-rose-700" : ""}>{dueStr}</b>
          </div>
        )}

        <div className="mt-1">
          <ResponsibleChip name={taskResponsibleName} isWeek={isWeek} />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24 }}
      className={[
        "rounded-2xl border bg-white shadow-sm hover:shadow-[0_10px_28px_rgba(16,24,40,0.10)] transition-shadow",
        overdue ? "border-rose-300 ring-1 ring-rose-200" : "border-gray-200",
      ].join(" ")}
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b bg-gradient-to-r from-white to-gray-50/50">
        <div className="flex items-center gap-2">
          {overdue && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              Atrasada
            </span>
          )}
          <div
            className={`w-1.5 h-6 rounded bg-gradient-to-b ${
              TYPE_ACCENT[type] || TYPE_ACCENT.default
            }`}
          />
          <TypePill type={type} />
        </div>
        <span
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${statusChip(
            task?.status
          )}`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="px-3 py-3 space-y-2 text-sm">
        <div className="font-semibold text-gray-600">{companyName}</div>
        <div className="flex items-center gap-2 text-gray-700">
          <HiCalendar className="w-4 h-4" />
          <b className={overdue ? "text-rose-700" : ""}>{dueStr}</b>
          <span className="text-gray-400">•</span>
          <span
            className={["tabular-nums", overdue ? "text-rose-700" : ""].join(
              " "
            )}
          >
            {tm.label}
          </span>
        </div>

        {task?.card?.customer?.name && (
          <div className="flex items-center gap-2 text-gray-700">
            <HiUser className="w-4 h-4" />
            <span className="truncate">{task.card.customer.name}</span>
          </div>
        )}

        <ResponsibleChip name={taskResponsibleName} />
      </div>

      <div className="px-3 pb-3 pt-1 flex flex-wrap gap-2">
        {hasCard ? (
          <Button size="xs" color="light" onClick={() => onOpenCard?.(task)}>
            Ver card
          </Button>
        ) : (
          <Button size="xs" color="light" onClick={() => onOpenCard?.(task)}>
            Editar tarefa
          </Button>
        )}
        {task.status === "open" && (
          <>
            <Button size="xs" color="success" onClick={() => onDone(task)}>
              <div className="flex gap-1 items-center">
                <HiCheck className="mr-1" />
                <div> Concluir</div>
              </div>
            </Button>
            <Button size="xs" color="failure" onClick={() => onCancel(task)}>
              <div className="flex gap-1 items-center">
                <HiX className="mr-1" /> <span>Cancelar</span>
              </div>
            </Button>
          </>
        )}
      </div>
    </motion.div>
  );
}

/* Linha por hora (modo Dia) */
function HourRow({
  hour,
  tasks,
  onDone,
  onCancel,
  onOpenCard,
  isCurrent,
  viewDateISO,
  showOverdue,
}) {
  return (
    <div className="grid grid-cols-[72px,1fr] sm:grid-cols-[88px,1fr] gap-3 sm:gap-4 py-3 relative">
      <div className="sticky left-0 -ml-2 sm:-ml-3 pl-2 sm:pl-3 pr-1 sm:pr-2 bg-white">
        <div
          className={`inline-flex items-center gap-2 rounded-full px-2 py-0.5 sm:px-2.5 sm:py-1 border text-[11px] sm:text-xs tabular-nums ${
            isCurrent
              ? "bg-gray-50 border-gray-200 text-gray-700"
              : "bg-gray-100 border-gray-200 text-gray-500"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              isCurrent ? "bg-green-500" : "bg-green-300"
            }`}
          />
          {pad2(hour)}:00
        </div>
      </div>

      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gray-100" />
        <div className="relative grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
          {(tasks || []).length === 0 ? (
            <div className="text-xs text-gray-300 italic">—</div>
          ) : (
            tasks.map((t) => (
              <CalendarTaskCard
                key={t.id}
                task={t}
                onDone={onDone}
                onCancel={onCancel}
                onOpenCard={onOpenCard}
                overdue={showOverdue && isOverdueTask(t, viewDateISO)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* Linha da tabela (modo Lista) */
function ListRowTr({ task, overdue, onDone, onCancel, onOpenCard }) {
  const tm = extractTime(task?.dueDate);
  const TypeIcon = TYPE_ICON[task?.type] || HiClock;
  const typeLabel = TYPE_LABEL[task?.type] || "Ação";
  const customer = getTaskDisplayTitle(task);
  const resp = task?.responsible?.name || task?.card?.responsible?.name || "";
  const hasCard = !!task?.card?.id;

  return (
    <tr className="odd:bg-white even:bg-gray-50 hover:bg-gray-100/60">
      <td className="px-3 py-2 align-middle whitespace-nowrap">
        <div className="flex gap-1 items-center">
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 tabular-nums mr-1.5">
            {toBRDate(task?.dueDate)}
          </span>
          <span className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-700 tabular-nums">
            {tm.label}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-gray-200 bg-white shrink-0">
            <TypeIcon className="w-3.5 h-3.5 text-gray-700" />
          </span>
          <span className="text-sm text-gray-900 font-medium truncate">
            {typeLabel}
          </span>
          {overdue && task?.status === "open" && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
              Atrasada
            </span>
          )}
          <div>
            <StatusPill status={task?.status} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        <div
          className="text-xs text-gray-800 truncate"
          style={{ maxWidth: "230px" }}
          title={customer}
        >
          {customer}
        </div>
      </td>
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <AvatarInitials name={resp} />
          <span
            className="text-sm text-gray-800 truncate"
            title={resp || "Não atribuído"}
          >
            {resp || "Não atribuído"}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-center">
        <div className="inline-flex items-center gap-1.5">
          {hasCard ? (
            <IconActionButton
              title="Ver card"
              onClick={() => onOpenCard?.(task)}
            >
              <HiViewList className="w-4 h-4" />
            </IconActionButton>
          ) : (
            <IconActionButton
              title="Editar tarefa"
              onClick={() => onOpenCard?.(task)}
            >
              <HiMiniPencilSquare className="w-4 h-4" />
            </IconActionButton>
          )}
          {task.status === "open" ? (
            <>
              <IconActionButton
                title="Concluir"
                color="success"
                onClick={() => onDone(task)}
              >
                <HiCheck className="w-4 h-4" />
              </IconActionButton>
              <IconActionButton
                title="Cancelar"
                color="failure"
                onClick={() => onCancel(task)}
              >
                <HiX className="w-4 h-4" />
              </IconActionButton>
            </>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

/* ============================================================
 * Página
 * ============================================================ */
function TaskCreateModal({
  open,
  onClose,
  form,
  setForm,
  onSubmit,
  loading,
  users,
  isEditing = false,
}) {
  if (!open) return null;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 py-6"
      style={{ zIndex: 999, background: "rgba(0,0,0,0.4)", top: "-20px" }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 sm:p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? "Editar tarefa" : "Nova tarefa"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {isEditing && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <Select
                value={form.status || "open"}
                onChange={handleChange("status")}
              >
                <option value="open">Aberta</option>
                <option value="done">Concluída</option>
                <option value="canceled">Cancelada</option>
              </Select>
            </div>
          )}

          {/* Tipo */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tipo</label>
            <Select value={form.type} onChange={handleChange("type")}>
              <option value="ligacao">Ligação</option>
              <option value="reuniao">Reunião</option>
              <option value="email">E-mail</option>
              <option value="visita">Visita</option>
              <option value="followup">Follow-up</option>
            </Select>
          </div>

          {/* Responsável */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Responsável
            </label>
            <Select
              value={form.responsibleId}
              onChange={handleChange("responsibleId")}
            >
              <option value="">Não atribuído</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>

          {/* Data / Hora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data</label>
              <TextInput
                type="date"
                value={form.dueDate || ""}
                onChange={handleChange("dueDate")}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hora</label>
              <TextInput
                type="time"
                value={form.dueTime || ""}
                onChange={handleChange("dueTime")}
              />
            </div>
          </div>

          {/* Nota / descrição */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Comentário / Nota
            </label>
            <textarea
              className="w-full rounded-lg border border-gray-300 text-sm p-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
              rows={3}
              value={form.note || ""}
              onChange={handleChange("note")}
              placeholder="Ex.: ligar para o cliente e confirmar proposta..."
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button color="light" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} isProcessing={loading}>
            {isEditing ? "Salvar alterações" : "Salvar tarefa"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TasksTodayPage() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [modalHistory, setModalHistory] = useState([]);
  const [modalStages, setModalStages] = useState([]);

  const [quickTag, setQuickTag] = useState(() => readFilters().quickTag ?? "");
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);

  const [type, setType] = useState(() => readFilters().type ?? "");
  const [responsibleId, setResponsibleId] = useState(
    () => readFilters().responsibleId ?? ""
  );
  const [q, setQ] = useState(() => readFilters().q ?? "");
  const [status, setStatus] = useState(() => readFilters().status ?? "open");

  // viewMode: "calendar" | "list"
  const [viewMode, setViewMode] = useState(
    () => readFilters().viewMode ?? "calendar"
  );
  // calendarMode: "day" | "week" | "month"
  const [calendarMode, setCalendarMode] = useState(
    () => readFilters().calendarMode ?? "day"
  );

  const defaultDate = readFilters().viewDate ?? toISODate(new Date());
  const [viewDateISO, setViewDateISO] = useState(defaultDate);
  const [noteDateISO, setNoteDateISO] = useState(defaultDate);
  const [dailyNote, setDailyNote] = useState("");
  const [dailyNoteLoading, setDailyNoteLoading] = useState(false);
  const [dailyNoteSaving, setDailyNoteSaving] = useState(false);
  const [dailyNoteSavedAt, setDailyNoteSavedAt] = useState(null);
  const [dailyNoteSavedBy, setDailyNoteSavedBy] = useState("");
  const [dailyNoteError, setDailyNoteError] = useState("");
  const [dailyNoteHistory, setDailyNoteHistory] = useState([]);
  const [dailyNoteHistoryLoading, setDailyNoteHistoryLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const { userPermission } = useApplication();
  const canEditDailyNote =
    userPermission === "ADMIN" || userPermission === "POWER_USER";

  // Voz -> Texto (nota diária)
  const dailyNoteRecorderRef = useRef(null);
  const dailyNoteStreamRef = useRef(null);
  const dailyNoteChunksRef = useRef([]);
  const dailyNoteAbortRef = useRef(false);
  const dailyNoteTimerRef = useRef(null);
  const [dailyNoteRecording, setDailyNoteRecording] = useState(false);
  const [dailyNoteLoadingVoice, setDailyNoteLoadingVoice] = useState(false);
  const [dailyNoteTranscribing, setDailyNoteTranscribing] = useState(false);
  const dailyNoteSpeechSupported =
    typeof window !== "undefined" &&
    navigator?.mediaDevices?.getUserMedia &&
    window.MediaRecorder;

  const clearDailyNoteTimer = useCallback(() => {
    if (dailyNoteTimerRef.current) {
      clearTimeout(dailyNoteTimerRef.current);
      dailyNoteTimerRef.current = null;
    }
    setDailyNoteLoadingVoice(false);
  }, []);

  const cleanupDailyNoteStream = useCallback(() => {
    if (dailyNoteStreamRef.current) {
      dailyNoteStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    dailyNoteStreamRef.current = null;
  }, []);

  const normalizeDailyNoteMime = useCallback((value = "") => {
    return String(value || "").split(";")[0].trim();
  }, []);

  const dailyNoteExtFromMime = useCallback(
    (value = "") => {
      const type = normalizeDailyNoteMime(value).toLowerCase();
      if (!type) return "webm";
      if (type.includes("webm")) return "webm";
      if (type.includes("ogg") || type.includes("oga")) return "ogg";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
      if (type.includes("wav")) return "wav";
      if (type.includes("flac")) return "flac";
      if (type.includes("m4a")) return "m4a";
      return "webm";
    },
    [normalizeDailyNoteMime]
  );

  const dailyNotePickMimeType = useCallback(() => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
  }, []);

  const stopDailyNoteRecording = useCallback(
    (options = {}) => {
      const { discard = false } = options;
      clearDailyNoteTimer();
      if (discard) dailyNoteAbortRef.current = true;
      const recorder = dailyNoteRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch (err) {
          console.warn("Falha ao parar gravação da nota diária:", err);
        }
      } else {
        dailyNoteAbortRef.current = false;
        cleanupDailyNoteStream();
        setDailyNoteRecording(false);
      }
    },
    [clearDailyNoteTimer, cleanupDailyNoteStream]
  );

  const transcribeDailyNote = useCallback(
    async (blob) => {
      setDailyNoteTranscribing(true);
      try {
        const fd = new FormData();
        const safeMime = normalizeDailyNoteMime(blob.type);
        const ext = dailyNoteExtFromMime(safeMime);
        fd.append("file", blob, `audio.${ext}`);
        const res = await axios.post(API.TRANSCRIBE, fd, {
          headers: { "Content-Type": undefined },
        });
        const text = res?.data?.text || res?.data?.data?.text || "";
        if (!text) {
          alert("Não foi possível transcrever o áudio.");
          return;
        }
        setDailyNote((prev) => {
          const html = toRichHtml(text);
          if (!prev) return html;
          return `${prev}${html}`;
        });
        setDailyNoteError("");
        setDailyNoteSavedAt(null);
        setDailyNoteSavedBy("");
      } catch (err) {
        console.error("Erro ao transcrever áudio:", err);
        alert(
          err?.response?.data?.message || "Falha ao transcrever áudio."
        );
      } finally {
        setDailyNoteTranscribing(false);
      }
    },
    [
      dailyNoteExtFromMime,
      normalizeDailyNoteMime,
    ]
  );

  const startDailyNoteRecording = useCallback(async () => {
    if (!dailyNoteSpeechSupported) {
      alert("Seu navegador não suporta gravação de áudio. Use Chrome ou Edge.");
      return;
    }
    if (dailyNoteRecorderRef.current) stopDailyNoteRecording({ discard: true });

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.warn("Erro ao acessar microfone:", err);
      alert("Permissão de microfone negada.");
      return;
    }

    dailyNoteStreamRef.current = stream;
    dailyNoteChunksRef.current = [];
    const mimeType = dailyNotePickMimeType();
    const recorder = new MediaRecorder(
      stream,
      mimeType ? { mimeType } : undefined
    );

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        dailyNoteChunksRef.current.push(event.data);
      }
    };

    recorder.onerror = (event) => {
      console.warn("Erro na gravação da nota diária:", event);
    };

    recorder.onstop = async () => {
      const discard = dailyNoteAbortRef.current;
      dailyNoteAbortRef.current = false;
      const chunks = dailyNoteChunksRef.current;
      dailyNoteChunksRef.current = [];
      cleanupDailyNoteStream();
      if (dailyNoteRecorderRef.current === recorder) {
        dailyNoteRecorderRef.current = null;
      }
      setDailyNoteRecording(false);
      if (discard || !chunks.length) return;
      const safeMime = normalizeDailyNoteMime(recorder.mimeType || "audio/webm");
      const blob = new Blob(chunks, { type: safeMime || "audio/webm" });
      await transcribeDailyNote(blob);
    };

    dailyNoteRecorderRef.current = recorder;
    setDailyNoteRecording(true);
    recorder.start();
  }, [
    cleanupDailyNoteStream,
    dailyNotePickMimeType,
    dailyNoteSpeechSupported,
    normalizeDailyNoteMime,
    stopDailyNoteRecording,
    transcribeDailyNote,
  ]);

  const startDailyNoteRecordingWithDelay = useCallback(() => {
    if (dailyNoteLoadingVoice || dailyNoteTranscribing) return;
    clearDailyNoteTimer();
    if (dailyNoteRecorderRef.current) stopDailyNoteRecording({ discard: true });
    setDailyNoteLoadingVoice(true);
    dailyNoteTimerRef.current = setTimeout(() => {
      setDailyNoteLoadingVoice(false);
      startDailyNoteRecording();
    }, 1500);
  }, [
    clearDailyNoteTimer,
    dailyNoteLoadingVoice,
    dailyNoteTranscribing,
    startDailyNoteRecording,
    stopDailyNoteRecording,
  ]);

  useEffect(
    () => () => stopDailyNoteRecording({ discard: true }),
    [stopDailyNoteRecording]
  );

  // Modal do card
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [modalCardId, setModalCardId] = useState(null);
  const [modalTaskId, setModalTaskId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalLabels, setModalLabels] = useState([]);

  const todayISO = useMemo(() => toISODate(new Date()), []);
  const isToday = viewDateISO === todayISO;
  const viewBR = useMemo(() => toBRDate(viewDateISO), [viewDateISO]);
  const noteDateBR = useMemo(() => toBRDate(noteDateISO), [noteDateISO]);
  const showOverdue = viewMode === "list" && isToday;

  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [createForm, setCreateForm] = useState({
    type: "followup",
    responsibleId: "",
    dueDate: viewDateISO, // já sugere o dia que está sendo visto
    dueTime: "09:00",
    note: "",
    status: "open",
  });
  const [createLoading, setCreateLoading] = useState(false);

  useEffect(() => {
    writeFilters({
      status,
      type,
      responsibleId,
      q,
      viewMode,
      calendarMode,
      viewDate: viewDateISO,
      quickTag,
    });
  }, [
    status,
    type,
    responsibleId,
    q,
    viewMode,
    calendarMode,
    viewDateISO,
    quickTag,
  ]);

  // refs para rolar até a hora atual (modo Dia)
  const listRef = useRef(null);
  const now = new Date();
  const nowHour = now.getHours();

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
      setUsers(res?.data?.data?.data || []);
    } catch (e) {
      console.error("Erro ao buscar usuários", e);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const base = { status, q, type, responsibleId };
      let params = { ...base };

      if (calendarMode === "day") {
        params.date = viewDateISO;
      } else if (calendarMode === "week") {
        params.rangeStart = startOfWeekISO(viewDateISO);
        params.rangeEnd = endOfWeekISO(viewDateISO);
      } else if (calendarMode === "month") {
        params.rangeStart = startOfMonthISO(viewDateISO);
        params.rangeEnd = endOfMonthISO(viewDateISO);
      }

      // remove vazios
      Object.keys(params).forEach((k) => {
        if (!params[k]) delete params[k];
      });

      const res = await axios.get(`${CRM.TODAY_TASKS}`, { params });
      setTasks(res?.data?.data || []);
    } catch (e) {
      console.error("Erro ao listar tarefas", e);
    } finally {
      setLoading(false);
    }
  };

  const loadDailyCommitteeNote = useCallback(async (date) => {
    if (!date) {
      setDailyNote("");
      setDailyNoteSavedAt(null);
      return;
    }
    setDailyNoteLoading(true);
    setDailyNoteError("");
    setDailyNoteSavedAt(null);
    setDailyNoteSavedBy("");
    setDailyNote("");
    try {
      const res = await axios.get(`${CRM.DAILY_COMMITTEE_NOTE}`, {
        params: { date },
      });
      const payload = res?.data?.data ?? res?.data ?? {};
      const normalized = toRichHtml(payload.note ?? "");
      setDailyNote(normalized);
      const savedAt =
        payload.updatedAt ||
        payload.savedAt ||
        payload.saved_at ||
        payload.createdAt;
      setDailyNoteSavedAt(savedAt ? new Date(savedAt) : null);
      setDailyNoteSavedBy(payload.updatedByName || "");
    } catch (error) {
      console.error("Erro ao carregar nota do comitê:", error);
      setDailyNote("");
      setDailyNoteError("Não foi possível carregar o comentário.");
      setDailyNoteSavedAt(null);
      setDailyNoteSavedBy("");
    } finally {
      setDailyNoteLoading(false);
    }
  }, []);

  const loadDailyCommitteeNoteHistory = useCallback(async (date) => {
    if (!date) {
      setDailyNoteHistory([]);
      return;
    }
    setDailyNoteHistoryLoading(true);
    try {
      const res = await axios.get(`${CRM.DAILY_COMMITTEE_NOTE_HISTORY}`, {
        params: { date },
      });
      setDailyNoteHistory(res?.data?.data || []);
    } catch (error) {
      console.error("Erro ao carregar histórico do comitê:", error);
      setDailyNoteHistory([]);
    } finally {
      setDailyNoteHistoryLoading(false);
    }
  }, []);

  // 2) Substitua seu fetchCardDetails por este
  const fetchCardDetails = async (cardId) => {
    if (!cardId) return null;
    try {
      setModalLoading(true);

      const [cardRes, histRes] = await Promise.all([
        axios.get(`${CRM.GET_CARD}/${cardId}/details`),
        axios.get(`${CRM.GET_CARD_HISTORY}/${cardId}/history`),
      ]);

      const fullRaw = cardRes?.data?.data || null;
      const history = histRes?.data?.data || [];

      // Descobre o funnelId do card (vários fallbacks)
      const funnelId = fullRaw?.stage?.funnelId;

      // Busca os stages via FUNIL (como no seu fetchFunnel)
      const stagesFromCard = await fetchStagesFromFunnel(funnelId);

      // Garante stageId dentro do objeto do card
      const detectedStageId =
        fullRaw?.stageId ??
        fullRaw?.stage_id ??
        fullRaw?.stage?.id ??
        fullRaw?.stage?.stageId ??
        fullRaw?.funnelStageId ??
        fullRaw?.currentStageId ??
        null;

      const fullCard = {
        ...fullRaw,
        stageId:
          detectedStageId ??
          (stagesFromCard.length ? stagesFromCard[0].id : null),
      };

      // (opcional) tags/labels
      const labelsFromCard =
        fullRaw?.availableTags ||
        fullRaw?.labels ||
        fullRaw?.tags ||
        fullRaw?.availableLabels ||
        [];

      return { fullCard, history, stagesFromCard, labelsFromCard };
    } catch (e) {
      console.error("Erro ao carregar detalhes do card", e);
      return null;
    } finally {
      setModalLoading(false);
    }
  };

  const openCardFromTask = async (task) => {
    const cardId = task?.card?.id;
    if (!cardId) {
      openCreateFromTask(task);
      return;
    }

    setModalCardId(cardId);
    setModalTaskId(task?.id || null);
    setCardModalOpen(true); // já abre; conteúdo aparece quando carregar

    const loaded = await fetchCardDetails(cardId);
    if (!loaded) return;

    const { fullCard, history, stagesFromCard, labelsFromCard } = loaded;

    // alimenta o modal com dados completos
    setSelectedTask(fullCard);
    setModalHistory(history);
    setModalStages(stagesFromCard);
    setModalLabels(labelsFromCard);
  };

  const closeCardModal = () => {
    setCardModalOpen(false);
    setSelectedTask(null);
    setModalHistory([]);
    setModalCardId(null);
    setModalTaskId(null);
    fetchTasks();
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, responsibleId, viewDateISO, calendarMode]);

  useEffect(() => {
    if (calendarMode === "day") {
      setNoteDateISO(viewDateISO);
    }
  }, [calendarMode, viewDateISO]);

  useEffect(() => {
    if (viewMode !== "calendar" || !noteDateISO) return;
    loadDailyCommitteeNote(noteDateISO);
  }, [viewMode, noteDateISO, loadDailyCommitteeNote]);

  useEffect(() => {
    if (!showNoteModal || !noteDateISO) return;
    loadDailyCommitteeNoteHistory(noteDateISO);
  }, [showNoteModal, noteDateISO, loadDailyCommitteeNoteHistory]);

  // rola para a hora atual quando carregar (só calendário-dia e se a data for hoje)
  useEffect(() => {
    if (viewMode !== "calendar" || calendarMode !== "day" || !isToday) return;
    const el = listRef.current;
    if (!el) return;
    const currentRow = el.querySelector(`[data-hour="${nowHour}"]`);
    if (currentRow) {
      const offset = currentRow.offsetTop - 80;
      el.scrollTo({ top: offset, behavior: "smooth" });
    }
  }, [loading, viewMode, calendarMode, isToday]); // após carregar/trocar modo

  const handleSearch = (e) => {
    e?.preventDefault?.();
    fetchTasks();
  };

  const handleDailyNoteChange = (valueOrEvent) => {
    const next =
      valueOrEvent?.target?.value !== undefined
        ? valueOrEvent.target.value
        : valueOrEvent;
    setDailyNote(next);
    setDailyNoteError("");
    setDailyNoteSavedAt(null);
    setDailyNoteSavedBy("");
  };

  const handleDailyNoteSave = async () => {
    if (!noteDateISO || dailyNoteSaving || !canEditDailyNote) {
      if (!canEditDailyNote) {
        setDailyNoteError("Somente ADMIN/POWER_USER pode salvar o comentário.");
      }
      return;
    }
    setDailyNoteSaving(true);
    setDailyNoteError("");
    try {
      const normalized = toRichHtml(dailyNote);
      const clean = stripVisualEmpty(normalized);
      const payload = { date: noteDateISO, note: clean };
      const res = await axios.post(`${CRM.DAILY_COMMITTEE_NOTE}`, payload);
      const responsePayload = res?.data?.data ?? res?.data ?? {};
      const savedAt =
        responsePayload.updatedAt ||
        responsePayload.savedAt ||
        responsePayload.saved_at ||
        responsePayload.createdAt;
      setDailyNoteSavedAt(savedAt ? new Date(savedAt) : new Date());
      setDailyNoteSavedBy(responsePayload.updatedByName || "");
      if (responsePayload.note !== undefined) {
        setDailyNote(toRichHtml(responsePayload.note ?? ""));
      }
      await loadDailyCommitteeNoteHistory(noteDateISO);
      setShowNoteModal(false);
    } catch (error) {
      console.error("Erro ao salvar nota do comitê diário:", error);
      setDailyNoteError("Não foi possível salvar o comentário.");
    } finally {
      setDailyNoteSaving(false);
    }
  };

  const handleSelectNoteDate = (dateISO, openModal = true) => {
    setNoteDateISO(dateISO);
    if (openModal) {
      setShowNoteModal(true);
    }
  };

  const handleOpenNoteModal = () => {
    if (!noteDateISO) {
      setNoteDateISO(viewDateISO);
    }
    setShowNoteModal(true);
  };

  const handleCloseNoteModal = () => {
    setShowNoteModal(false);
  };

  // Navegação dependente do modo (dia/semana/mês)
  const setToday = () => setViewDateISO(todayISO);
  const shiftByMode = (delta) => {
    if (calendarMode === "day") {
      setViewDateISO(addDays(viewDateISO, delta));
    } else if (calendarMode === "week") {
      setViewDateISO(addDays(viewDateISO, delta * 7));
    } else {
      // mês
      const d = fromISODateLocal(viewDateISO);
      d.setMonth(d.getMonth() + delta);
      setViewDateISO(toISODate(d));
    }
  };

  const closeCreateModal = () => {
    setCreateTaskOpen(false);
    setEditingTaskId(null);
  };

  const openCreateFromTask = (task) => {
    if (!task) return;

    const dueIso = task?.dueDate || null;
    const tm = extractTime(dueIso);
    const responsibleId =
      task?.responsibleId ||
      task?.responsible?.id ||
      task?.card?.responsible?.id ||
      "";
    const cleanNote =
      typeof task?.note === "string" ? task.note.replace(/<[^>]+>/g, "") : "";

    setCreateForm({
      type: task?.type || "followup",
      responsibleId: responsibleId ? String(responsibleId) : "",
      dueDate: dueIso ? toISODate(dueIso) : viewDateISO,
      dueTime: tm.hasTime ? tm.label : "09:00",
      note: cleanNote,
      status: task?.status || "open",
    });
    setEditingTaskId(task?.id || null);
    setCreateTaskOpen(true);
  };

  const createTask = async () => {
    try {
      setCreateLoading(true);

      let dueIso = null;
      if (createForm.dueDate) {
        const timeStr = createForm.dueTime || "09:00"; // ex: "09:00"
        // manda no formato "YYYY-MM-DDTHH:mm:00"
        // sem 'Z', sem conversão pra UTC
        dueIso = `${createForm.dueDate}T${timeStr}:00`;
      }

      const payload = {
        type: createForm.type || "followup",
        responsibleId: createForm.responsibleId || null,
        dueDate: dueIso,
        note: createForm.note || null,
        // cardId: null // explicitamente sem card
      };

      if (editingTaskId) {
        const updatePayload = {
          ...payload,
          status: createForm.status || "open",
        };
        await axios.put(`${CRM.UPDATE_TASK}/${editingTaskId}`, updatePayload);
        const respUser = users.find(
          (u) => String(u.id) === String(createForm.responsibleId)
        );
        setTasks((prev) =>
          prev.map((t) =>
            t.id === editingTaskId
              ? {
                  ...t,
                  ...updatePayload,
                  responsible: respUser
                    ? { id: respUser.id, name: respUser.name }
                    : t.responsible,
                }
              : t
          )
        );
        closeCreateModal();
        setCreateForm((old) => ({ ...old, note: "" }));
        return;
      }

      const res = await axios.post(`${CRM.POSTPONE_TASK}`, payload);
      const newTask = res?.data?.data || res?.data;

      // adiciona na lista local (ou chama fetchTasks())
      setTasks((prev) => [...prev, newTask]);

      closeCreateModal();
      setCreateForm((old) => ({
        ...old,
        note: "",
        // mantém tipo/responsável/dia se quiser
      }));
    } catch (e) {
      console.error(
        editingTaskId ? "Erro ao atualizar tarefa" : "Erro ao criar tarefa",
        e
      );
      // aqui você pode plugar toast
    } finally {
      setCreateLoading(false);
    }
  };

  // ações
  const markDone = async (t) => {
    try {
      await axios.post(`${CRM.COMPLETE_TASK}/${t.id}/complete`);
    } catch (e) {
      console.error("Erro ao concluir", e);
    } finally {
      setTasks((prev) =>
        prev.map((i) =>
          i.id === t.id
            ? { ...i, status: "done", completedAt: new Date().toISOString() }
            : i
        )
      );
    }
  };
  const cancelTask = async (t) => {
    try {
      await axios.post(`${CRM.CANCEL_TASK}/${t.id}/cancel`);
      setTasks((prev) =>
        prev.map((i) => (i.id === t.id ? { ...i, status: "canceled" } : i))
      );
    } catch (e) {
      console.error("Erro ao cancelar", e);
    }
  };
  const updateTask = async (taskId, patch) => {
    try {
      // await axios.patch(`${CRM.TASKS}/${taskId}`, patch);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t))
      );
    } catch (e) {
      console.error("Erro ao atualizar tarefa", e);
    }
  };

  // aceita (cardId, targetStageId) OU ({ cardId, toStageId })
  const onMoveStageFromModal = async (...args) => {
    try {
      let cardId = null;
      let targetStageId = null;

      if (args.length === 2) {
        [cardId, targetStageId] = args;
      } else if (args[0] && typeof args[0] === "object") {
        const payload = args[0];
        cardId = payload.cardId ?? payload.id ?? payload.card?.id ?? null;
        targetStageId =
          payload.toStageId ?? payload.stageId ?? payload.targetStageId ?? null;
      }

      if (!cardId || !targetStageId) return;

      // Se existir endpoint real pra mover:
      // await axios.post(`${CRM.CARDS}/${cardId}/move`, { stageId: targetStageId });

      // Após mover, atualize modal + lista
      await onRefetchCard();
      await fetchTasks();
    } catch (e) {
      console.error("Erro ao mover card", e);
    }
  };

  const onRefetchCard = async () => {
    if (!modalCardId) return;
    const loaded = await fetchCardDetails(modalCardId);
    if (!loaded) return;
    const { fullCard, history, stagesFromCard, labelsFromCard } = loaded;
    setSelectedTask(fullCard);
    setModalHistory(history);
    setModalStages(stagesFromCard);
    setModalLabels(labelsFromCard);
    // também é legal atualizar a lista/contadores
    fetchTasks();
  };

  const onCardDeleted = (deletedCardId) => {
    setTasks((prev) => prev.filter((t) => t?.card?.id !== deletedCardId));
    closeCardModal();
  };

  // Filtros de status para contadores/visões
  // 1) Contadores brutos (do resultado carregado, antes de quickTag)
  const openCount = tasks.filter((t) => t.status === "open").length;
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const canceledCount = tasks.filter((t) => t.status === "canceled").length;

  // 2) Contadores dependentes da data de visão
  const dueTodayOpenCount = tasks.filter((t) =>
    isDueToday(t, viewDateISO)
  ).length;
  const overdueOpenCount = showOverdue
    ? tasks.filter((t) => isOverdueTask(t, viewDateISO)).length
    : 0;

  // 3) Filtro base por status (mantido como já existia)
  const statusFiltered =
    status === "all" ? tasks : tasks.filter((t) => t.status === status);

  // 4) Filtro da quickTag (liga/desliga)
  const matchesQuick = (t) => {
    if (quickTag === "open") return t.status === "open";
    if (quickTag === "today") return isDueToday(t, viewDateISO);
    if (quickTag === "overdue")
      return showOverdue && isOverdueTask(t, viewDateISO);
    if (quickTag === "done") return t.status === "done";
    return true;
  };
  const filtered = statusFiltered.filter(matchesQuick);

  /* ====== Preparos por modo ====== */

  // DIA
  const { byHour, noTime, hoursWindow } = useMemo(
    () => groupByHour(filtered.filter((t) => sameDay(t?.dueDate, viewDateISO))),
    [filtered, viewDateISO]
  );
  const hours = useMemo(() => {
    const [start, end] = hoursWindow;
    const list = [];
    for (let h = start; h <= end; h++) list.push(h);
    return list;
  }, [hoursWindow]);
  const showNow =
    viewMode === "calendar" &&
    calendarMode === "day" &&
    isToday &&
    now.getHours() >= hours[0] &&
    now.getHours() <= hours[hours.length - 1];

  // SEMANA
  const weekStartISO = startOfWeekISO(viewDateISO);
  const weekEndISO = endOfWeekISO(viewDateISO);
  const weekDays = useMemo(() => {
    const arr = [];
    let c = fromISODateLocal(weekStartISO);
    for (let i = 0; i < 7; i++) {
      arr.push(toISODate(c));
      c.setDate(c.getDate() + 1);
    }
    return arr;
  }, [weekStartISO]);
  const tasksByDayWeek = useMemo(() => {
    const map = new Map();
    weekDays.forEach((d) => map.set(d, []));
    filtered.forEach((t) => {
      const d = toISODate(t?.dueDate || t?.createdAt || new Date());
      if (d >= weekStartISO && d <= weekEndISO) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(t);
      }
    });
    // ordenar por hora
    weekDays.forEach((d) =>
      map.get(d)?.sort((a, b) => {
        const ta = +new Date(a?.dueDate || 0);
        const tb = +new Date(b?.dueDate || 0);
        return ta - tb;
      })
    );
    return map;
  }, [filtered, weekDays, weekStartISO, weekEndISO]);

  // MÊS
  const monthMatrix = useMemo(
    () => buildMonthMatrix(viewDateISO),
    [viewDateISO]
  );
  const firstMonthDay = startOfMonthISO(viewDateISO);
  const lastMonthDay = endOfMonthISO(viewDateISO);
  const tasksByDayMonth = useMemo(() => {
    const map = new Map();
    monthMatrix.flat().forEach((d) => map.set(d, []));
    filtered.forEach((t) => {
      const d = toISODate(t?.dueDate || t?.createdAt || new Date());
      if (d >= monthMatrix[0][0] && d <= monthMatrix.at(-1)?.at(-1)) {
        if (!map.has(d)) map.set(d, []);
        map.get(d).push(t);
      }
    });
    monthMatrix.flat().forEach((d) =>
      map.get(d)?.sort((a, b) => {
        const ta = +new Date(a?.dueDate || 0);
        const tb = +new Date(b?.dueDate || 0);
        return ta - tb;
      })
    );
    return map;
  }, [filtered, monthMatrix]);

  /* ====== Render ====== */
  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      {/* Hero */}
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div
          className="p-4 sm:p-6 md:p-7"
          style={{
            background:
              "linear-gradient(90deg,rgb(73, 73, 73) 0%,rgb(91, 91, 91) 100%)",
          }}
        >
          <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <HiOutlineSparkles className="opacity-90" />
                <span className="truncate">
                  Sua agenda{" "}
                  {viewMode === "calendar"
                    ? calendarMode === "day"
                      ? "— Dia"
                      : calendarMode === "week"
                      ? "— Semana"
                      : "— Mês"
                    : "— Lista"}
                </span>
              </div>
              <h1 className="text-white text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">
                {viewBR}
              </h1>
            </div>

            {/* Navegação de período / modos */}
            <div className="flex items-stretch gap-2 flex-wrap justify-end">
              {!isToday && (
                <Button
                  size="sm"
                  color="light"
                  onClick={setToday}
                  className="!bg-white/90 !text-[#7A082D] hover:!bg-white"
                >
                  Hoje
                </Button>
              )}

              <Button
                size="sm"
                color="light"
                onClick={() => {
                  setEditingTaskId(null);
                  setCreateForm((old) => ({
                    ...old,
                    dueDate: viewDateISO,
                    note: "",
                    status: "open",
                  }));
                  setCreateTaskOpen(true);
                }}
                className="!bg-white !text-[#7A082D] hover:!bg-gray-100"
              >
                + Nova tarefa
              </Button>

              <div className="flex items-center rounded-lg overflow-hidden border border-white/30 bg-white/10">
                <Tooltip content="Anterior" style="light">
                  <button
                    type="button"
                    onClick={() => shiftByMode(-1)}
                    className="px-2 py-2 text-white/90 hover:bg-white/10"
                    title="Anterior"
                  >
                    <HiChevronLeft className="w-5 h-5" />
                  </button>
                </Tooltip>

                <div className="hidden xs:flex items-center gap-2 px-2">
                  <HiCalendar className="text-white" />
                  <input
                    type="date"
                    className="bg-transparent text-white placeholder-white text-sm outline-none border-0 focus:ring-0 [color-scheme:dark]"
                    value={viewDateISO}
                    onChange={(e) => setViewDateISO(e.target.value)}
                    aria-label="Selecionar data"
                  />
                </div>

                <Tooltip content="Próximo" style="light">
                  <button
                    type="button"
                    onClick={() => shiftByMode(1)}
                    className="px-2 py-2 text-white/90 hover:bg-white/10"
                    title="Próximo"
                  >
                    <HiChevronRight className="w-5 h-5" />
                  </button>
                </Tooltip>
              </div>

              {/* Toggle de visualização */}
              <div className="inline-flex rounded-lg overflow-hidden border border-white/30 bg-white/10">
                <button
                  type="button"
                  onClick={() => setViewMode("calendar")}
                  className={[
                    "px-3 py-1.5 text-sm flex items-center gap-1",
                    viewMode === "calendar"
                      ? "bg-white text-[#7A082D]"
                      : "text-white/90 hover:bg-white/10",
                  ].join(" ")}
                  title="Visualização em calendário"
                >
                  <HiCalendarDays />{" "}
                  <span className="hidden sm:inline">Calendário</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={[
                    "px-3 py-1.5 text-sm flex items-center gap-1",
                    viewMode === "list"
                      ? "bg-white text-[#7A082D]"
                      : "text-white/90 hover:bg-white/10",
                  ].join(" ")}
                  title="Visualização em lista"
                >
                  <HiViewList /> <span className="hidden sm:inline">Lista</span>
                </button>
              </div>

              {/* Subtoggle do calendário: Dia / Semana / Mês */}
              {viewMode === "calendar" && (
                <div className="inline-flex rounded-lg overflow-hidden border border-white/30 bg-white/10">
                  {[
                    { key: "day", label: "Dia" },
                    { key: "week", label: "Semana" },
                    { key: "month", label: "Mês" },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setCalendarMode(opt.key)}
                      className={[
                        "px-3 py-1.5 text-sm",
                        calendarMode === opt.key
                          ? "bg-white text-[#7A082D]"
                          : "text-white/90 hover:bg-white/10",
                      ].join(" ")}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 sm:mt-4 flex flex-wrap gap-2">
            {/* 🔵 Abertas */}
            <button
              type="button"
              onClick={() => setQuickTag((t) => (t === "today" ? "" : "today"))}
              className={[
                "inline-flex items-center gap-2 text-xs sm:text-sm px-2.5 py-1.5 rounded-full border transition",
                quickTag === "today"
                  ? "bg-white text-[#7A082D] border-white"
                  : "bg-white/10 text-white border-white/20 hover:bg-white/20",
              ].join(" ")}
              title="Filtrar por tarefas que vencem na data atual (abertas)"
            >
              <span className="w-2 h-2 rounded-full bg-blue-200" />
              Vencendo Hoje: <b>{dueTodayOpenCount}</b>
            </button>

            {/* 🔴 Vencidas (abertas e atrasadas em relação à data vista) */}
            <button
              type="button"
              onClick={() =>
                setQuickTag((t) => (t === "overdue" ? "" : "overdue"))
              }
              className={[
                "inline-flex items-center gap-2 text-xs sm:text-sm px-2.5 py-1.5 rounded-full border transition",
                quickTag === "overdue"
                  ? "bg-white text-[#7A082D] border-white"
                  : "bg-white/10 text-white border-white/20 hover:bg-white/20",
              ].join(" ")}
              title="Filtrar por Vencidas"
            >
              <span className="w-2 h-2 rounded-full bg-red-200" />
              Vencidas: <b>{overdueOpenCount}</b>
            </button>

            {/* (mantive os contadores existentes – se quiser, pode remover estes dois) */}
            <button
              type="button"
              onClick={() => setQuickTag((t) => (t === "done" ? "" : "done"))}
              className={[
                "inline-flex items-center gap-2 text-xs sm:text-sm px-2.5 py-1.5 rounded-full border transition",
                quickTag === "done"
                  ? "bg-white text-[#7A082D] border-white"
                  : "bg-white/10 text-white border-white/20 hover:bg-white/20",
              ].join(" ")}
              title="Filtrar por tarefas que vencem na data atual (abertas)"
            >
              <span className="w-2 h-2 rounded-full bg-green-200" />
              Concluídas: <b>{doneCount}</b>
            </button>
          </div>
        </div>

        {/* Barra de filtros */}
        <div className="bg-white p-3 sm:p-4 md:p-5">
          <form onSubmit={handleSearch} className="flex gap-3 sm:gap-4">
            {/* Tipo */}
            <div className="flex-0">
              <label className="block text-xs text-gray-500 mb-1">Tipo</label>
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Todos</option>
                <option value="ligacao">Ligação</option>
                <option value="reuniao">Reunião</option>
                <option value="email">E-mail</option>
                <option value="visita">Visita</option>
                <option value="followup">Follow-up</option>
              </Select>
            </div>

            {/* Responsável */}
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs text-gray-500 mb-1">
                Responsável
              </label>
              <Select
                value={responsibleId}
                onChange={(e) => setResponsibleId(e.target.value)}
              >
                <option value="">Todos</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Busca */}
            <div className="col-span-2 sm:col-span-1 flex-1">
              <label className="block text-xs text-gray-500 mb-1">Buscar</label>
              <div className="flex gap-2">
                <TextInput
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cliente, nota..."
                  className="flex-1"
                  icon={HiSearch}
                />
                <Button type="submit" className="shrink-0">
                  Buscar
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Botão do comitê diário */}
      {viewMode === "calendar" && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={handleOpenNoteModal}>
            Comitê interno — {noteDateBR || toBRDate(viewDateISO)}
          </Button>
        </div>
      )}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          dateBR={
            viewMode === "calendar"
              ? calendarMode === "week"
                ? `${toBRDate(weekStartISO)} a ${toBRDate(weekEndISO)}`
                : calendarMode === "month"
                ? `${toBRDate(firstMonthDay)} a ${toBRDate(lastMonthDay)}`
                : viewBR
              : viewBR
          }
        />
      ) : viewMode === "list" ? (
        // ======== LIST/TABLE VIEW ========
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50 text-sm text-gray-600">
            Data: {viewBR}
          </div>
          <div className="overflow-y-auto overflow-x-auto">
            <table className="min-w-[900px] table-fixed w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-40">
                    Prazo
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-48">
                    Tipo
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600">
                    Cliente/Empresa
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-left text-[11px] font-semibold text-gray-600 w-60">
                    Responsável
                  </th>
                  <th className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur px-3 py-2 text-center text-[11px] font-semibold text-gray-600 w-44">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[...filtered]
                  .sort(
                    (a, b) =>
                      +new Date(a?.dueDate || 0) - +new Date(b?.dueDate || 0)
                  )
                  .map((t) => (
                    <ListRowTr
                      key={t.id}
                      task={t}
                      onDone={markDone}
                      onCancel={cancelTask}
                      onOpenCard={openCardFromTask}
                      overdue={showOverdue && isOverdueTask(t, viewDateISO)}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : calendarMode === "day" ? (
        // ======== CALENDÁRIO — DIA ========
        <div className="rounded-2xl border border-gray-200 bg-white">
          {/* Cabeçalho */}
          <div className="grid grid-cols-[72px,1fr] sm:grid-cols-[88px,1fr] gap-3 sm:gap-4 items-center px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50">
            <div className="sticky left-0 -ml-2 sm:-ml-3 pl-2 sm:pl-3 pr-1 sm:pr-2">
              <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-gray-400">
                Horário
              </span>
            </div>
            <div className="text-xs text-gray-500">Dia — {viewBR}</div>
          </div>

          {/* Lista rolável */}
          <div
            ref={listRef}
            className="relative px-3 md:px-4 py-2"
            style={{ maxHeight: "72vh", overflowY: "auto" }}
          >
            {showNow && (
              <div
                className="absolute left-[72px] sm:left-[88px] right-3 md:right-4 z-10"
                style={{
                  top: `calc((${
                    nowHour - hours[0]
                  } * 64px) + (${now.getMinutes()} / 60) * 64px + 8px)`,
                }}
              >
                <div className="relative">
                  <div className="absolute -left-2 w-2 h-2 rounded-full bg-rose-600 shadow" />
                  <div className="h-px bg-rose-500/70" />
                </div>
              </div>
            )}

            <div className="space-y-1">
              {hours.map((h) => (
                <div key={h} data-hour={h}>
                  <HourRow
                    hour={h}
                    isCurrent={isToday && h === nowHour}
                    tasks={(byHour.get(h) || []).sort((a, b) => {
                      const am = extractTime(a.dueDate).minute ?? 0;
                      const bm = extractTime(b.dueDate).minute ?? 0;
                      return am - bm;
                    })}
                    onDone={markDone}
                    onCancel={cancelTask}
                    onOpenCard={openCardFromTask}
                    viewDateISO={viewDateISO}
                    showOverdue={showOverdue}
                  />
                </div>
              ))}
            </div>

            {/* Sem horário */}
            {noTime?.length ? (
              <div className="mt-4">
                <div className="flex items-center gap-2 text-gray-700 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide">
                    Sem horário
                  </span>
                  <span className="text-xs text-gray-400">
                    ({noTime.length})
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3">
                  {noTime.map((t) => (
                    <CalendarTaskCard
                      key={t.id}
                      task={t}
                      onDone={markDone}
                      onCancel={cancelTask}
                      onOpenCard={openCardFromTask}
                      overdue={showOverdue && isOverdueTask(t, viewDateISO)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : calendarMode === "week" ? (
        // ======== CALENDÁRIO — SEMANA ========
        <div className="rounded-2xl border border-gray-200 bg-gray-100 overflow-hidden">
          <div className="px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50 text-xs text-gray-500 bg-white">
            Semana — {toBRDate(weekStartISO)} a {toBRDate(weekEndISO)}
          </div>

          <div className="overflow-x-auto">
            {/* grade sempre com 7 colunas; largura mínima pra não quebrar no mobile */}
            <div className="min-w-[980px] sm:min-w-[1120px] grid grid-cols-7 gap-2 sm:gap-3 p-2 sm:p-3">
              {weekDays.map((dayISO) => {
                const list = tasksByDayWeek.get(dayISO) || [];
                const label = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"][
                  (fromISODateLocal(dayISO).getDay() + 6) % 7
                ];
                const isTodayCol = sameDay(dayISO, todayISO);
                const isSelectedNoteDay = sameDay(dayISO, noteDateISO);

                return (
                  <div
                    key={dayISO}
                    className={[
                      "rounded-xl border bg-white p-2 sm:p-3 flex flex-col gap-2",
                      isTodayCol ? "border-[#7A082D]" : "border-gray-200",
                      isSelectedNoteDay ? "ring-2 ring-[#7A082D]/40" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] sm:text-xs uppercase text-gray-500">
                        {label}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectNoteDate(dayISO);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] text-[#7A082D] border border-[#7A082D] rounded-full px-2 py-0.5 hover:bg-[#7A082D]/5"
                        >
                          <HiMiniPencilSquare className="w-3 h-3" />
                          Nota
                        </button>
                        <div
                          className={[
                            "text-[12px] sm:text-sm font-semibold",
                            isTodayCol ? "text-[#7A082D]" : "text-gray-800",
                          ].join(" ")}
                        >
                          {toBRDate(dayISO)}
                        </div>
                      </div>
                    </div>

                    {list.length === 0 ? (
                      <div className="text-xs text-gray-300 italic">—</div>
                    ) : (
                      <div className="space-y-2">
                        {list.map((t) => (
                          <CalendarTaskCard
                            key={t.id}
                            task={t}
                            compact
                            overdue={showOverdue && isOverdueTask(t, dayISO)}
                            onOpenCard={openCardFromTask}
                            onCancel={cancelTask}
                            onDone={markDone}
                            isWeek
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        // ======== CALENDÁRIO — MÊS ========
        <div className="rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
          <div className="px-3 md:px-4 py-3 border-b bg-gradient-to-r from-white to-gray-50 text-xs text-gray-500">
            Mês — {toBRDate(firstMonthDay)} a {toBRDate(lastMonthDay)}
          </div>

          {/* Cabeçalho dos dias */}
          <div className="grid grid-cols-7 text-[10px] sm:text-[11px] text-gray-500 px-2 sm:px-3 pt-3">
            {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map((d) => (
              <div key={d} className="px-1 pb-2">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 p-2 sm:p-3 pt-2 bg-gray-50">
            {monthMatrix.map((week, wi) =>
              week.map((dayISO, di) => {
                const inMonth =
                  dayISO >= firstMonthDay && dayISO <= lastMonthDay;
                const list = tasksByDayMonth.get(dayISO) || [];
                const isTodayCell = sameDay(dayISO, todayISO);
                const isSelectedNoteDay = sameDay(dayISO, noteDateISO);

                return (
                  <div
                    key={`${wi}-${di}`}
                    className={[
                      "min-h-[110px] sm:min-h-[120px] rounded-lg border p-2 flex flex-col gap-1",
                      isTodayCell
                        ? "border-black bg-rose-50/30"
                        : inMonth
                        ? "border-gray-200 bg-white"
                        : "border-gray-100 bg-gray-50/40",
                      isSelectedNoteDay ? "ring-2 ring-[#7A082D]/40" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={[
                          "text-[11px] sm:text-xs font-semibold inline-flex items-center justify-center w-6 h-6 rounded-full",
                          isTodayCell
                            ? "bg-black text-white ring-2 ring-[#7A082D]/40"
                            : inMonth
                            ? "text-gray-800"
                            : "text-gray-400",
                        ].join(" ")}
                      >
                        {fromISODateLocal(dayISO).getDate()}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectNoteDate(dayISO);
                          }}
                          className="inline-flex items-center gap-1 text-[10px] text-[#7A082D] border border-[#7A082D] rounded-full px-2 py-0.5 hover:bg-[#7A082D]/5"
                        >
                          <HiMiniPencilSquare className="w-3 h-3" />
                          Nota
                        </button>
                        {!!list.length && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 border border-gray-200 text-gray-700">
                            {list.length}
                          </span>
                        )}
                      </div>
                    </div>

                    {list.length ? (
                      <div className="space-y-1 mt-1">
                        {list.slice(0, 3).map((t) => {
                          const tm = extractTime(t?.dueDate);
                          const TypeIcon = TYPE_ICON[t?.type] || HiClock;
                          return (
                            <div
                              key={t.id}
                              className="text-[10px] sm:text-[11px] rounded-lg border bg-white px-2 py-1 flex items-center gap-1 cursor-pointer hover:shadow-sm"
                              onClick={() => openCardFromTask(t)}
                              title={t?.card?.id ? "Ver card" : "Editar tarefa"}
                            >
                              <TypeIcon className="w-3.5 h-3.5 text-gray-600" />
                              <span className="tabular-nums text-gray-700">
                                {tm.hasTime ? tm.label : ""}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="truncate text-gray-800">
                                {t?.card?.lead?.name ||
                                  t?.card?.lead?.companyName ||
                                  "Sem informação"}
                              </span>
                            </div>
                          );
                        })}
                        {list.length > 3 && (
                          <div className="text-[10px] text-gray-500">
                            +{list.length - 3} mais
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[11px] text-gray-300 italic">—</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ===== MODAL DO COMITÊ ===== */}
      {cardModalOpen && modalCardId && (
        <TaskModal
          task={selectedTask}
          onClose={closeCardModal}
          updateTask={updateTask}
          history={modalHistory}
          setHistory={setModalHistory}
          setSelectedTask={setSelectedTask}
          stages={(modalStages || []).sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0)
          )}
          onMoveStage={onMoveStageFromModal}
          onRefetchCard={onRefetchCard}
          onCardDeleted={onCardDeleted}
          users={users}
          labels={modalLabels}
          loading={modalLoading} // se o TaskModal exibe spinner
        />
      )}

      {showNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed z-60 top-0 bottom-0 left-0 right-0 bg-black opacity-50"
            style={{ zIndex: "70" }}
          />
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-xl"
            style={{ zIndex: "80" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Comitê interno — {noteDateBR || toBRDate(viewDateISO)}
                </h3>
                <p className="text-xs text-gray-500">
                  {calendarMode === "day"
                    ? `Notas do dia ${noteDateBR || viewBR}.`
                    : noteDateBR
                    ? `Notas de ${noteDateBR}. Clique em um dia no calendário para trocar.`
                    : "Selecione um dia no calendário para ver a nota."}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseNoteModal}
                className="rounded-full border border-gray-200 p-1 text-gray-500 hover:bg-gray-100"
              >
                <HiX className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 space-y-2 text-[12px] text-gray-500">
              {dailyNoteLoading && (
                <div className="text-gray-500">Carregando comentário…</div>
              )}
              {dailyNoteError && (
                <div className="text-rose-500">{dailyNoteError}</div>
              )}
              {!dailyNoteLoading && dailyNoteSavedAt && (
                <div className="text-emerald-600">
                  Salvo em{" "}
                  {dailyNoteSavedAt.toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {dailyNoteSavedBy && <> por {dailyNoteSavedBy}</>}
                </div>
              )}
              {!canEditDailyNote && (
                <div className="text-rose-600">
                  Somente ADMIN/POWER_USER pode editar o comentário.
                </div>
              )}
            </div>

            <div
              className="mt-3"
              style={{ maxHeight: "250px", overflowY: "auto" }}
            >
              {canEditDailyNote ? (
                <>
                  <TiptapEditor
                    value={dailyNote}
                    onChange={handleDailyNoteChange}
                    users={(users || []).map((u) => ({
                      id: u.id,
                      value: u.name || u.email || "Usuário",
                      email: u.email,
                      avatar: u.avatarUrl,
                    }))}
                    placeholder="Ex.: revisamos pendências de CRM, alinhamos prioridades e definimos os próximos passos. Use @ para mencionar."
                    minHeight={140}
                    className={
                      dailyNoteLoading ? "opacity-60 pointer-events-none" : ""
                    }
                  />
                  <div className="mt-2 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={
                        dailyNoteRecording
                          ? () => stopDailyNoteRecording()
                          : () => startDailyNoteRecordingWithDelay()
                      }
                      disabled={
                        !dailyNoteSpeechSupported ||
                        dailyNoteLoadingVoice ||
                        dailyNoteTranscribing ||
                        dailyNoteLoading
                      }
                      title={
                        dailyNoteSpeechSupported
                          ? dailyNoteRecording
                            ? "Parar gravação"
                            : dailyNoteLoadingVoice
                            ? "Preparando gravação"
                            : dailyNoteTranscribing
                            ? "Transcrevendo áudio"
                            : "Voz -> Texto"
                          : "Gravação de áudio indisponível"
                      }
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                        dailyNoteRecording
                          ? "border-red-300 bg-red-50 text-red-700"
                          : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                      } ${
                        !dailyNoteSpeechSupported
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      <HiMicrophone size={14} />
                      {dailyNoteLoadingVoice
                        ? "Carregando…"
                        : dailyNoteTranscribing
                        ? "Transcrevendo…"
                        : dailyNoteRecording
                        ? "Parar"
                        : "Voz -> Texto"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                  <div
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: dailyNote || "<p>—</p>",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-gray-200 pt-3">
              <div className="text-sm font-semibold text-gray-700">
                Histórico de alterações
              </div>
              {dailyNoteHistoryLoading ? (
                <div className="mt-2 text-xs text-gray-500">
                  Carregando histórico…
                </div>
              ) : dailyNoteHistory.length ? (
                <div className="mt-2 space-y-2">
                  {dailyNoteHistory.map((h) => {
                    const author = h?.updater?.name || "Sistema";
                    const when = h?.createdAt
                      ? new Date(h.createdAt).toLocaleString("pt-BR")
                      : "";
                    return (
                      <div
                        key={h.id}
                        className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                      >
                        <div className="text-[11px] text-gray-500">
                          {author}
                          {when ? ` • ${when}` : ""}
                        </div>
                        <div className="mt-1 text-gray-700">
                          {stripHtmlPreview(h.note)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-2 text-xs text-gray-400">
                  Ainda não há histórico.
                </div>
              )}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <Button
                size="sm"
                color="light"
                onClick={handleCloseNoteModal}
                disabled={dailyNoteSaving}
              >
                Fechar
              </Button>
              <Button
                size="sm"
                onClick={handleDailyNoteSave}
                disabled={
                  dailyNoteLoading ||
                  dailyNoteSaving ||
                  !canEditDailyNote ||
                  !noteDateISO
                }
                isProcessing={dailyNoteSaving}
              >
                {dailyNoteSaving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {createTaskOpen && (
        <TaskCreateModal
          open={createTaskOpen}
          onClose={closeCreateModal}
          form={createForm}
          setForm={setCreateForm}
          onSubmit={createTask}
          loading={createLoading}
          users={users}
          isEditing={!!editingTaskId}
        />
      )}
    </div>
  );
}
